import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap,
  ShieldAlert,
  Flame,
  Copy,
  Check,
  History,
  Trash2,
  ChevronRight,
  Skull,
  DollarSign,
  Target,
  Radar,
  Languages,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { SAMPLE_EMAILS } from "../lib/samples";
import LanguagePicker from "../components/LanguagePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFESSIONS = [
  "Software Development",
  "Graphic Design & UI/UX",
  "Copywriting & Content",
  "Video Editing & Motion Graphics",
  "Social Media Management",
];

const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "HUF", symbol: "Ft" },
];

const EXPERIENCE = [
  { value: "mid", label: "Mid-weight" },
  { value: "senior", label: "Senior Pro" },
  { value: "top1", label: "Top 1% Specialist" },
];

const TONES = [
  { value: "aggressive", label: "Merciless Shark" },
  { value: "consultative", label: "Consultative Value-Add" },
  { value: "polite", label: "Polite & Firm" },
];

const HISTORY_KEY = "ratebeast_history_v1";

const fmtMoney = (n, code) => {
  const sym = CURRENCIES.find((c) => c.code === code)?.symbol || "$";
  const rounded = Number(n || 0);
  const str =
    code === "HUF"
      ? rounded.toLocaleString("hu-HU", { maximumFractionDigits: 0 })
      : rounded.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return code === "HUF" ? `${str} ${sym}` : `${sym}${str}`;
};

const SEVERITY_STYLES = {
  critical: "border-[#FF2E4D] bg-[rgba(255,46,77,0.12)] text-[#FF6E85]",
  high: "border-[#FF2E4D]/70 bg-[rgba(255,46,77,0.08)] text-[#FF8AA0]",
  medium: "border-[#FFB020]/70 bg-[rgba(255,176,32,0.08)] text-[#FFCF6E]",
  low: "border-zinc-700 bg-zinc-900/60 text-zinc-300",
};

export default function RateBeast() {
  const [profession, setProfession] = useState(PROFESSIONS[0]);
  const [currency, setCurrency] = useState("USD");
  const [experience, setExperience] = useState("senior");
  const [tone, setTone] = useState("aggressive");
  const [language, setLanguage] = useState("auto");
  const [emailText, setEmailText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
      setHistory(Array.isArray(stored) ? stored : []);
    } catch {
      setHistory([]);
    }
  }, []);

  useEffect(() => {
    if (!loading) return;
    const msgs = [
      "Intercepting client transmission...",
      "Deconstructing scope creep vectors...",
      "Calculating Mr. Wonderful leverage multiple...",
      "Detonating exposure traps...",
      "Synthesizing shark clause...",
    ];
    let i = 0;
    setStatusMsg(msgs[0]);
    const id = setInterval(() => {
      i = (i + 1) % msgs.length;
      setStatusMsg(msgs[i]);
    }, 1400);
    return () => clearInterval(id);
  }, [loading]);

  const persistHistory = (entry) => {
    const next = [entry, ...history].slice(0, 10);
    setHistory(next);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(HISTORY_KEY);
    toast.success("Session history purged.");
  };

  const runAnalyze = async () => {
    if (emailText.trim().length < 20) {
      toast.error("Paste at least a few sentences from the client.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/analyze`, {
        profession,
        currency,
        tone,
        experience,
        language,
        email_text: emailText,
      });
      setResult(data);
      persistHistory({
        id: data.id,
        created_at: data.created_at,
        profession,
        currency,
        language,
        audacity_score: data.audacity_score,
        snippet: emailText.slice(0, 120),
        data,
      });
      toast.success("Threat analysis complete.");
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e.message || "Analysis failed. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadSample = (key) => {
    setEmailText(SAMPLE_EMAILS[key].text);
    toast.message(`Loaded sample: ${SAMPLE_EMAILS[key].label}`);
  };

  const restoreEntry = (entry) => {
    setResult(entry.data);
    setEmailText(entry.snippet);
    setProfession(entry.profession);
    setCurrency(entry.currency);
    if (entry.language) setLanguage(entry.language);
    toast.success("Analysis restored.");
  };

  const copyOffer = async () => {
    if (!result?.counter_offer_email) return;
    try {
      const text = `Subject: ${result.counter_offer_subject}\n\n${result.counter_offer_email}`;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Counter-offer copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed.");
    }
  };

  const gaugeColor = useMemo(() => {
    const s = result?.audacity_score || 0;
    if (s >= 70) return "#FF2E4D";
    if (s >= 40) return "#FFB020";
    return "#00F59B";
  }, [result]);

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden bg-[#08080A] text-white"
      data-testid="ratebeast-root"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-40 border-b border-[#1E1E28] bg-[#08080A]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-8">
          <div className="flex items-center gap-3">
            <div className="relative flex h-10 w-10 items-center justify-center rounded-md border border-[#FF2E4D]/50 bg-[#15151C] rb-crimson-glow">
              <Skull className="h-5 w-5 text-[#FF2E4D]" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-xl font-black uppercase tracking-tight sm:text-2xl">
                Rate<span className="text-[#FF2E4D]">Beast</span>{" "}
                <span className="text-zinc-500 text-sm font-mono-tech">AI</span>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-zinc-500">
                <span className="rb-radar-dot inline-block h-1.5 w-1.5 rounded-full bg-[#FF2E4D]" />
                Threat Radar Online
              </div>
            </div>
          </div>

          <Sheet>
            <SheetTrigger asChild>
              <Button
                data-testid="history-drawer-toggle"
                variant="outline"
                className="border-[#1E1E28] bg-[#0F0F13] text-zinc-200 hover:bg-[#15151C] hover:text-white"
              >
                <History className="h-4 w-4" />
                <span className="hidden sm:inline">Dossiers</span>
                <span className="ml-1 rounded bg-[#FF2E4D]/20 px-1.5 py-0.5 text-[10px] font-mono-tech text-[#FF2E4D]">
                  {history.length}
                </span>
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-full max-w-md border-l border-[#1E1E28] bg-[#0B0B0E] text-white"
            >
              <SheetHeader>
                <SheetTitle className="font-display uppercase tracking-wide text-white">
                  Session Dossiers
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-3 max-h-[75vh] overflow-y-auto rb-scroll pr-1">
                {history.length === 0 && (
                  <div className="rounded-md border border-[#1E1E28] bg-[#0F0F13] p-6 text-center text-sm text-zinc-500">
                    No prior interrogations. Fire your first analysis.
                  </div>
                )}
                {history.map((h) => (
                  <button
                    key={h.id}
                    data-testid="history-item-entry"
                    onClick={() => restoreEntry(h)}
                    className="group w-full rounded-md border border-[#1E1E28] bg-[#0F0F13] p-4 text-left transition hover:border-[#FF2E4D]/60 hover:bg-[#15151C]"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                        {new Date(h.created_at).toLocaleString()}
                      </span>
                      <span
                        className="font-mono-tech text-sm font-bold"
                        style={{
                          color:
                            h.audacity_score >= 70
                              ? "#FF2E4D"
                              : h.audacity_score >= 40
                                ? "#FFB020"
                                : "#00F59B",
                        }}
                      >
                        {h.audacity_score}%
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-zinc-300 line-clamp-2">
                      {h.snippet}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500">
                      <span>{h.profession}</span>
                      <ChevronRight className="ml-auto h-4 w-4 opacity-0 transition group-hover:opacity-100" />
                    </div>
                  </button>
                ))}
                {history.length > 0 && (
                  <Button
                    onClick={clearHistory}
                    variant="ghost"
                    className="w-full text-[#FF2E4D] hover:bg-[#FF2E4D]/10 hover:text-[#FF6E85]"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Purge History
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#1E1E28]">
        <div className="tactical-grid absolute inset-0 opacity-60" />
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#FF2E4D]/20 blur-[120px]" />
        <div className="absolute -bottom-24 right-0 h-72 w-72 rounded-full bg-[#FF2E4D]/10 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-8 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF2E4D]/40 bg-[#FF2E4D]/5 px-3 py-1 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF2E4D]">
            <Flame className="h-3 w-3" />
            Kevin O'Leary Hardball Engine
          </div>
          <h1 className="font-display mt-5 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            Stop leaving money
            <br />
            <span className="text-[#FF2E4D]">on the table.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-sm text-zinc-400 sm:text-base">
            Analyze client emails, expose hidden red flags, and instantly
            generate killer counter-offers to charge what you are actually
            worth.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Exposure Trap Exposer", "Scope Creep Disinfectant", "Net-90 Killer"].map(
              (b) => (
                <span
                  key={b}
                  className="rounded-full border border-[#1E1E28] bg-[#0F0F13] px-3 py-1 text-[11px] font-mono-tech uppercase tracking-wider text-zinc-400"
                >
                  {b}
                </span>
              ),
            )}
          </div>
        </div>
      </section>

      {/* WORKSPACE */}
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8 sm:py-14">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,45%)_minmax(0,55%)]">
          {/* LEFT PANEL */}
          <section
            className="rounded-xl border border-[#1E1E28] bg-[#0F0F13] p-5 sm:p-6 rb-bezel"
            data-testid="input-panel"
          >
            <div className="mb-5 flex items-center gap-2">
              <Radar className="h-4 w-4 text-[#FF2E4D]" />
              <h2 className="font-display text-lg font-extrabold uppercase tracking-widest text-zinc-100 sm:text-xl">
                Client Transmission
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                  Profession
                </label>
                <Select value={profession} onValueChange={setProfession}>
                  <SelectTrigger
                    data-testid="profession-select-trigger"
                    className="h-11 border-[#1E1E28] bg-[#0B0B0E] text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[#1E1E28] bg-[#0F0F13] text-white">
                    {PROFESSIONS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                  Currency
                </label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger
                    data-testid="currency-select-trigger"
                    className="h-11 border-[#1E1E28] bg-[#0B0B0E] text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[#1E1E28] bg-[#0F0F13] text-white">
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.code} ({c.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                  Experience
                </label>
                <Select value={experience} onValueChange={setExperience}>
                  <SelectTrigger
                    data-testid="experience-select-trigger"
                    className="h-11 border-[#1E1E28] bg-[#0B0B0E] text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[#1E1E28] bg-[#0F0F13] text-white">
                    {EXPERIENCE.map((e) => (
                      <SelectItem key={e.value} value={e.value}>
                        {e.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                  Counter-Offer Tone
                </label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger
                    data-testid="tone-select-trigger"
                    className="h-11 border-[#1E1E28] bg-[#0B0B0E] text-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-[#1E1E28] bg-[#0F0F13] text-white">
                    {TONES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-5">
              <label className="mb-2 flex items-center justify-between text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                <span className="flex items-center gap-1.5">
                  <Languages className="h-3 w-3 text-[#FF2E4D]" />
                  Counter-Offer Language
                </span>
                <span className="text-zinc-600 normal-case tracking-normal">
                  25 languages
                </span>
              </label>
              <LanguagePicker value={language} onChange={setLanguage} />
            </div>

            <div className="mt-5">
              <label className="mb-2 flex items-center justify-between text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                <span>Client Message</span>
                <span className="text-zinc-600">{emailText.length} chars</span>
              </label>
              <textarea
                data-testid="client-email-textarea"
                value={emailText}
                onChange={(e) => setEmailText(e.target.value)}
                placeholder="Paste the client's email, message, project description, or contract draft here..."
                className="rb-scroll min-h-[220px] w-full resize-y rounded-md border border-[#1E1E28] bg-[#0B0B0E] p-4 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-[#FF2E4D] focus:ring-1 focus:ring-[#FF2E4D]/50"
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                data-testid="sample-pill-exposure"
                onClick={() => loadSample("exposure")}
                className="rounded-full border border-[#1E1E28] bg-[#15151C] px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-wider text-zinc-300 transition hover:border-[#FF2E4D]/60 hover:text-white"
              >
                Exposure Trap
              </button>
              <button
                data-testid="sample-pill-scope"
                onClick={() => loadSample("scope")}
                className="rounded-full border border-[#1E1E28] bg-[#15151C] px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-wider text-zinc-300 transition hover:border-[#FF2E4D]/60 hover:text-white"
              >
                Scope Creep Rush
              </button>
              <button
                data-testid="sample-pill-unlimited"
                onClick={() => loadSample("revisions")}
                className="rounded-full border border-[#1E1E28] bg-[#15151C] px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-wider text-zinc-300 transition hover:border-[#FF2E4D]/60 hover:text-white"
              >
                Unlimited Revisions
              </button>
              <button
                data-testid="sample-pill-delayed"
                onClick={() => loadSample("delayed")}
                className="rounded-full border border-[#1E1E28] bg-[#15151C] px-3 py-1.5 text-[11px] font-mono-tech uppercase tracking-wider text-zinc-300 transition hover:border-[#FF2E4D]/60 hover:text-white"
              >
                Net-90 Ambush
              </button>
            </div>

            <button
              data-testid="analyze-secure-profit-button"
              onClick={runAnalyze}
              disabled={loading}
              className="group mt-6 flex w-full items-center justify-center gap-3 rounded-md border border-[#FF2E4D] bg-[#FF2E4D] px-6 py-4 font-display text-base font-black uppercase tracking-wider text-white transition hover:bg-[#ff485f] hover:rb-crimson-glow disabled:opacity-60 sm:text-lg"
            >
              {loading ? (
                <span className="font-mono-tech text-xs tracking-widest">
                  {statusMsg}
                </span>
              ) : (
                <>
                  <Zap className="h-5 w-5" strokeWidth={2.5} />
                  Analyze &amp; Secure Profit
                </>
              )}
            </button>
            {loading && (
              <div className="relative mt-3 h-1 w-full overflow-hidden rounded bg-[#15151C] rb-scan-bar" />
            )}
          </section>

          {/* RIGHT PANEL */}
          <section
            className="rounded-xl border border-[#1E1E28] bg-[#0F0F13] p-5 sm:p-6 rb-bezel"
            data-testid="output-panel"
          >
            {!result && !loading && <EmptyState />}
            {loading && <LoadingState statusMsg={statusMsg} />}
            {result && !loading && (
              <div className="space-y-5">
                {/* CARD 1: AUDACITY */}
                <div
                  data-testid="card-audacity-index"
                  className="rounded-lg border border-[#FF2E4D]/40 bg-[#15151C] p-5 rb-crimson-glow"
                >
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-[#FF2E4D]" />
                    <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF2E4D]">
                      Card 01 · Red Flags &amp; Audacity Index
                    </span>
                  </div>

                  <div className="mt-4 flex items-end justify-between gap-4">
                    <div>
                      <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                        Audacity Score
                      </div>
                      <div
                        data-testid="audacity-score-value"
                        className="font-mono-tech text-5xl font-black leading-none sm:text-6xl"
                        style={{ color: gaugeColor }}
                      >
                        {result.audacity_score}%
                      </div>
                    </div>
                    <div className="text-right max-w-[55%]">
                      <div className="font-display text-lg font-extrabold uppercase leading-tight text-white sm:text-xl">
                        {result.verdict_headline}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="relative h-2 w-full overflow-hidden rounded-full bg-[#0B0B0E]">
                      <div className="rb-gauge-track absolute inset-0 opacity-30" />
                      <div
                        className="relative h-full rounded-full"
                        style={{
                          width: `${Math.max(4, Math.min(100, result.audacity_score))}%`,
                          background: gaugeColor,
                          boxShadow: `0 0 12px ${gaugeColor}`,
                        }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between font-mono-tech text-[10px] uppercase tracking-widest text-zinc-600">
                      <span>Clean</span>
                      <span>Sketchy</span>
                      <span>Predatory</span>
                    </div>
                  </div>

                  {result.shark_quote && (
                    <blockquote className="mt-4 border-l-2 border-[#FF2E4D] bg-[#0B0B0E] px-4 py-3 text-sm italic text-zinc-200">
                      "{result.shark_quote}"
                      <div className="mt-1 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF2E4D]">
                        — RateBeast
                      </div>
                    </blockquote>
                  )}

                  <ul
                    data-testid="red-flags-list"
                    className="mt-5 space-y-2"
                  >
                    {result.red_flags?.length === 0 && (
                      <li className="rounded border border-[#00F59B]/30 bg-[#00F59B]/5 p-3 text-sm text-[#00F59B]">
                        No major traps detected. Still, get everything in
                        writing.
                      </li>
                    )}
                    {result.red_flags?.map((f, idx) => (
                      <li
                        key={idx}
                        className={`rounded border p-3 text-sm ${SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.medium}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="font-display text-sm font-bold uppercase tracking-wider text-white">
                            🚩 {f.title}
                          </div>
                          <span className="shrink-0 rounded bg-black/40 px-2 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest">
                            {f.severity}
                          </span>
                        </div>
                        {f.quote && (
                          <div className="mt-1 text-xs italic text-zinc-400">
                            “{f.quote}”
                          </div>
                        )}
                        <div className="mt-2 text-sm text-zinc-200">
                          {f.explanation}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CARD 2: MARKET VALUE */}
                <div
                  data-testid="card-fair-market-value"
                  className="rounded-lg border border-[#00F59B]/30 bg-[#15151C] p-5"
                >
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-[#00F59B]" />
                    <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#00F59B]">
                      Card 02 · Fair Market Value
                    </span>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded border border-[#1E1E28] bg-[#0B0B0E] p-4">
                      <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                        Low Anchor
                      </div>
                      <div className="mt-1 font-mono-tech text-2xl font-bold text-zinc-200">
                        {fmtMoney(result.fair_market_low, result.currency)}
                      </div>
                    </div>
                    <div
                      data-testid="price-range-display"
                      className="rounded border border-[#00F59B]/40 bg-[#0B0B0E] p-4 rb-emerald-glow"
                    >
                      <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-[#00F59B]">
                        Target Range
                      </div>
                      <div className="mt-1 font-mono-tech text-2xl font-black text-[#00F59B]">
                        {fmtMoney(result.fair_market_low, result.currency)} —{" "}
                        {fmtMoney(result.fair_market_high, result.currency)}
                      </div>
                    </div>
                    <div className="rounded border border-[#1E1E28] bg-[#0B0B0E] p-4">
                      <div className="flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-[0.2em] text-[#FF2E4D]">
                        <Target className="h-3 w-3" />
                        Walk-Away
                      </div>
                      <div className="mt-1 font-mono-tech text-2xl font-bold text-[#FF2E4D]">
                        {fmtMoney(result.walk_away_threshold, result.currency)}
                      </div>
                    </div>
                  </div>

                  {result.leverage_rationale && (
                    <p className="mt-4 text-sm leading-relaxed text-zinc-300">
                      <span className="font-display uppercase tracking-wider text-zinc-500">
                        Leverage:
                      </span>{" "}
                      {result.leverage_rationale}
                    </p>
                  )}
                </div>

                {/* CARD 3: COUNTER OFFER */}
                <div
                  data-testid="card-counter-offer"
                  className="rounded-lg border border-[#1E1E28] bg-[#15151C] p-5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Flame className="h-4 w-4 text-[#FF2E4D]" />
                      <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-zinc-400">
                        Card 03 · Shark-Style Counter-Offer
                      </span>
                    </div>
                    <button
                      data-testid="copy-counter-offer-button"
                      onClick={copyOffer}
                      className={`flex items-center gap-2 rounded-md border px-3 py-1.5 font-mono-tech text-[11px] uppercase tracking-widest transition ${
                        copied
                          ? "border-[#00F59B] bg-[#00F59B]/10 text-[#00F59B]"
                          : "border-[#FF2E4D] bg-[#FF2E4D]/10 text-[#FF6E85] hover:bg-[#FF2E4D]/20"
                      }`}
                    >
                      {copied ? (
                        <>
                          <Check className="h-3.5 w-3.5" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" /> Copy Email
                        </>
                      )}
                    </button>
                  </div>

                  <div className="mt-4 rounded border border-[#1E1E28] bg-[#0B0B0E] p-4">
                    <div className="mb-3 border-b border-[#1E1E28] pb-3">
                      <div className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-zinc-500">
                        Subject
                      </div>
                      <div className="mt-1 font-display text-base font-bold text-white">
                        {result.counter_offer_subject}
                      </div>
                    </div>
                    <pre
                      data-testid="counter-offer-content"
                      dir={language === "ar" ? "rtl" : "ltr"}
                      className="rb-scroll max-h-[500px] overflow-auto whitespace-pre-wrap break-words font-mono-tech text-xs leading-relaxed text-zinc-200 sm:text-sm"
                    >
                      {result.counter_offer_email}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>

      <footer className="border-t border-[#1E1E28] py-8 text-center text-xs text-zinc-600">
        <span className="font-mono-tech uppercase tracking-[0.3em]">
          RateBeast AI · Charge what you are worth.
        </span>
      </footer>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="empty-state"
      className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center"
    >
      <div className="relative mb-6 flex h-24 w-24 items-center justify-center rounded-full border border-[#FF2E4D]/40 bg-[#0B0B0E]">
        <Radar className="h-10 w-10 text-[#FF2E4D]" />
        <span className="rb-radar-dot absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#FF2E4D]" />
      </div>
      <h3 className="font-display text-xl font-black uppercase tracking-wider text-white">
        Awaiting Client Transmission
      </h3>
      <p className="mt-2 max-w-sm text-sm text-zinc-500">
        Paste an incoming client email to initiate deep leverage
        reconnaissance. RateBeast will surface every trap and draft your
        counter-offer.
      </p>
      <div className="mt-6 grid grid-cols-3 gap-3 text-center">
        {[
          { l: "Traps Detected", v: "0" },
          { l: "Audacity", v: "—" },
          { l: "Fair Value", v: "—" },
        ].map((s) => (
          <div
            key={s.l}
            className="rounded border border-[#1E1E28] bg-[#0B0B0E] px-3 py-2"
          >
            <div className="font-mono-tech text-lg font-bold text-zinc-300">
              {s.v}
            </div>
            <div className="text-[9px] font-mono-tech uppercase tracking-widest text-zinc-600">
              {s.l}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingState({ statusMsg }) {
  return (
    <div
      data-testid="loading-state"
      className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center"
    >
      <div className="relative mb-6 flex h-24 w-24 items-center justify-center rounded-full border border-[#FF2E4D]/60 bg-[#0B0B0E] rb-crimson-glow">
        <Radar className="h-10 w-10 animate-spin text-[#FF2E4D]" />
      </div>
      <div className="font-mono-tech text-xs uppercase tracking-[0.3em] text-[#FF2E4D]">
        {statusMsg || "Scanning..."}
      </div>
      <div className="relative mt-6 h-1 w-64 overflow-hidden rounded bg-[#15151C] rb-scan-bar" />
    </div>
  );
}
